import axios from 'axios';
import { captureExceptionSafe } from './captureExceptionSafe';
import { getTokens, setTokens } from './tokens';

const STATUS_CODES = {
  UNAUTHORIZED: 401,
  FORBIDDEN: 403,
};

let tokenRefreshCallback = null;

export function setTokenRefreshCallback(callback) {
  tokenRefreshCallback = callback;
}

export function createApiInstance(baseURL) {
  const opts = {
    baseURL,
    headers: {},
    withCredentials: true,
  };

  const tokens = getTokens();

  if (tokens) {
    if (!tokens.token) {
      // Track when tokens exist but token is missing/null
      captureExceptionSafe(
        new Error('API instance initialized with null token'),
        {
          tags: {
            component: 'api-instance',
            operation: 'missing-token',
          },
          extra: {
            hasTokensObject: Boolean(tokens),
            hasRefreshToken: Boolean(tokens?.refresh_token),
            tokenKeys: tokens ? Object.keys(tokens) : null,
          },
        },
      );
    } else {
      opts.headers.Authorization = `Bearer ${tokens.token}`;
    }
  } else {
    // Track when no tokens are available during API instance creation
    captureExceptionSafe(new Error('API instance initialized without tokens'), {
      tags: {
        component: 'api-instance',
        operation: 'no-tokens',
      },
      extra: {
        baseURL,
      },
    });
  }

  const instance = axios.create(opts);

  // When a request is sent along with { headers: { retry: true } },
  // get that value from the configured headers, set it in a custom field,
  // and delete the entry from the headers. This is the only way to get
  // a flag passed along with Axios via interceptors since custom fields
  // in AxiosRequestConfig are automatically ignored by the axios library.
  instance.interceptors.request.use(config => {
    const { retry } = config.headers;
    if (retry) {
      // Since the headers was repurposed to pass along the retry param,
      // delete the entry from the headers and set it within a custom field
      // of the config. This field will eventually make its way to the response
      // interceptor for the instance.
      config.custom = { retry };
      delete config.headers.retry;
    }
    return config;
  });

  // When a request fails and a refresh token is available, attempt to refresh
  // and obtain a new access token. If that's successful, save the new access
  // token and use it to retry the original request.
  instance.interceptors.response.use(null, async error => {
    const retryCodes = [STATUS_CODES.FORBIDDEN, STATUS_CODES.UNAUTHORIZED];

    const tokens = getTokens();

    const shouldRetry = Boolean(
      error.config &&
        error.response &&
        error.config.custom &&
        error.config.custom.retry &&
        retryCodes.includes(error.response.status) &&
        tokens?.refresh_token &&
        tokens.refresh_token !== null &&
        tokens.refresh_token !== '' &&
        tokens.refresh_token !== 'null',
    );

    if (!shouldRetry) {
      // Track when retry is skipped and why
      if (
        error.config?.custom?.retry &&
        retryCodes.includes(error.response?.status)
      ) {
        const skipReason = !tokens
          ? 'no-tokens'
          : !tokens.refresh_token
            ? 'no-refresh-token'
            : tokens.refresh_token === null
              ? 'null-refresh-token'
              : tokens.refresh_token === ''
                ? 'empty-refresh-token'
                : tokens.refresh_token === 'null'
                  ? 'string-null-refresh-token'
                  : 'unknown';

        captureExceptionSafe(new Error('Auth retry skipped'), {
          tags: {
            component: 'auth-interceptor',
            operation: 'retry-skipped',
          },
          extra: {
            skipReason,
            errorStatus: error.response?.status,
            errorUrl: error.config?.url,
            hasRetryFlag: Boolean(error.config?.custom?.retry),
            hasTokens: Boolean(tokens),
            refreshTokenType: tokens?.refresh_token
              ? typeof tokens.refresh_token
              : null,
            refreshTokenValue:
              tokens?.refresh_token === 'null'
                ? 'string-null'
                : tokens?.refresh_token === null
                  ? 'actual-null'
                  : tokens?.refresh_token === ''
                    ? 'empty-string'
                    : tokens?.refresh_token
                      ? 'has-value'
                      : 'falsy',
          },
        });
      }
      return Promise.reject(error);
    }

    try {
      if (!tokens) {
        throw error;
      }

      // Request a new access token
      const { data } = await axios.post(
        `${baseURL}/user/api-token-auth/refresh`,
        {
          refresh_token: tokens.refresh_token,
        },
      );

      // New set of tokens - use new refresh_token from response if provided, otherwise keep existing
      const newTokens = {
        token: data.token,
        refresh_token: data.refresh_token || tokens.refresh_token,
      };

      // Save the new tokens to localStorage
      setTokens(newTokens);

      // Notify Redux store of token update
      if (tokenRefreshCallback) {
        tokenRefreshCallback(newTokens);
      }

      const { config } = error;

      // Set the new token into the instance headers
      instance.defaults.headers.Authorization = `Bearer ${newTokens.token}`;

      // Set the new token into the request that will be retried
      config.headers.Authorization = `Bearer ${newTokens.token}`;

      // Retry the original request
      return axios.request(config);
    } catch (refreshError) {
      // Refresh endpoint 401 means the refresh token is no longer valid
      // (for example, it expired). Mark the original request error so callers
      // can distinguish this from other auth/bootstrap failures.
      if (refreshError?.response?.status === STATUS_CODES.UNAUTHORIZED) {
        error.isRefreshTokenExpired = true;
      }

      // Track refresh failures for monitoring
      captureExceptionSafe(refreshError, {
        tags: {
          component: 'auth-interceptor',
          operation: 'token-refresh-failed',
        },
        extra: {
          originalErrorStatus: error?.response?.status,
          originalErrorUrl: error?.config?.url,
          refreshErrorStatus: refreshError?.response?.status,
          refreshErrorMessage: refreshError?.message,
          hasRefreshToken: Boolean(tokens?.refresh_token),
          refreshTokenExpired:
            refreshError?.response?.status === STATUS_CODES.UNAUTHORIZED,
        },
      });

      // NOTE Since something went wrong when retrying, throwing the error for
      // the original request (which is what the user would expect).
      throw error;
    }
  });

  return instance;
}
